import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import UKHealthWidget from "@/components/admin/UKHealthWidget";
import api from "@/lib/api";
import { Loader2, Save, ExternalLink, AlertTriangle, RotateCcw } from "lucide-react";

/**
 * /admin/uk-health — owner-only page combining the health widget with a
 * config form. The owner pastes their current Emergent Universal Key
 * balance + auto-recharge settings (we can't read those from inside this
 * app — they live on the Emergent dashboard).
 */
export default function UKHealthPage() {
  const { user, loading } = useAuth();
  const [cfg, setCfg] = useState({
    balance_credits: 0,
    auto_recharge_enabled: true,
    auto_recharge_trigger: 50,
    auto_recharge_amount: 50,
  });
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [serviceHealth, setServiceHealth] = useState(null);
  const [clearing, setClearing] = useState(null);
  const [cleared, setCleared] = useState("");

  useEffect(() => {
    api.get("/admin/uk-health").then(({ data }) => {
      if (data.config) {
        setCfg({
          balance_credits: data.config.balance_credits ?? 0,
          auto_recharge_enabled: data.config.auto_recharge_enabled ?? true,
          auto_recharge_trigger: data.config.auto_recharge_trigger ?? 50,
          auto_recharge_amount: data.config.auto_recharge_amount ?? 50,
        });
      }
    }).catch(() => {});
    // Pull /api/health/service so we can show degraded-flag state + clear button
    api.get("/health/service").then(({ data }) => setServiceHealth(data)).catch(() => {});
  }, [reloadKey]);

  const onClearFlag = async (service) => {
    setClearing(service); setCleared("");
    try {
      await api.post(`/health/service/${service}/clear-degraded`);
      setCleared(service);
      setTimeout(() => setCleared(""), 2500);
      const { data } = await api.get("/health/service");
      setServiceHealth(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setClearing(null);
    }
  };

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;

  const onSave = async () => {
    setErr(""); setSaved(false); setSaving(true);
    try {
      await api.post("/admin/uk-health", cfg);
      setSaved(true);
      setReloadKey((k) => k + 1);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title={{ eyebrow: "Universal Key", heading: "UK Health & Auto-Recharge" }}
                subtitle="Track Universal Key balance, video burn rate, and verify your auto-recharge config is safe before customer-facing sessions.">
      <div className="space-y-6 max-w-4xl">
        <div key={reloadKey}><UKHealthWidget /></div>

        {/* Service-flag card — reads /api/health/service. The customer-facing
            banner reads the same flag. Lets the owner clear a stuck "degraded"
            state with one click (no Sora render required). */}
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-5 sm:p-6" data-testid="service-health-card">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">Service Health Flags</p>
              <h3 className="font-display text-2xl font-extrabold gold-shimmer">Customer-facing banner state</h3>
              <p className="text-xs text-[#A0A0A0] mt-1 max-w-2xl">
                These flags drive the warning banner customers see. A failed Sora call flips a flag to <em>degraded</em>; the next successful Sora call flips it back to <em>ok</em>. Flags auto-clear after 6 hours, or you can clear them now.
              </p>
            </div>
          </div>

          {!serviceHealth ? (
            <p className="text-xs text-[#5A5A5A]">Loading service health…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(serviceHealth.services || {}).map(([service, info]) => {
                const degraded = info.status === "degraded";
                const label = service.replace("_gen", "").replace(/^./, c => c.toUpperCase());
                return (
                  <div key={service} data-testid={`service-flag-${service}`}
                    className={`border p-3 ${degraded ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-bold ${degraded ? "text-red-300" : "text-emerald-300"}`}>{label}</p>
                      <span className={`text-[10px] uppercase tracking-wider ${degraded ? "text-red-300" : "text-emerald-300"}`}>
                        {degraded ? "Degraded" : "OK"}
                      </span>
                    </div>
                    {degraded && (
                      <>
                        <p className="text-[11px] text-[#A0A0A0] mt-1.5 line-clamp-2">Since {info.since?.slice(0, 16) || "—"}</p>
                        <button
                          onClick={() => onClearFlag(service)}
                          disabled={clearing === service}
                          data-testid={`clear-flag-${service}`}
                          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50"
                        >
                          {clearing === service ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                          Clear stuck flag
                        </button>
                      </>
                    )}
                    {cleared === service && (
                      <p className="text-[10px] text-emerald-300 mt-2" data-testid={`cleared-${service}`}>✓ Cleared · banner will refresh</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {serviceHealth?.overall === "degraded" && (
            <div className="mt-4 flex items-start gap-2 p-3 border border-amber-500/30 bg-amber-500/5 text-amber-200 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>If your Universal Key is funded and you've fixed the cause, click "Clear stuck flag" above — the customer-facing banner will disappear without waiting for a successful render.</span>
            </div>
          )}
        </div>

        <div className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-5 sm:p-6" data-testid="uk-config-form">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">Sync from Emergent</p>
              <h3 className="font-display text-2xl font-extrabold gold-shimmer">Current Universal Key state</h3>
              <p className="text-xs text-[#A0A0A0] mt-1">
                We can't read these values from inside YBM — paste what you see on the Emergent UK page so our health checks are accurate.
              </p>
            </div>
            <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[#F0C84B] inline-flex items-center gap-1 shrink-0">
              Open Emergent <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Current balance (credits)">
              <input type="number" step="0.01" min={0} value={cfg.balance_credits}
                data-testid="uk-balance"
                onChange={(e) => setCfg({ ...cfg, balance_credits: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]" />
            </Field>
            <Field label="Auto-recharge enabled?">
              <select data-testid="uk-auto-enabled" value={cfg.auto_recharge_enabled ? "yes" : "no"}
                onChange={(e) => setCfg({ ...cfg, auto_recharge_enabled: e.target.value === "yes" })}
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]">
                <option value="yes">Yes — auto-recharge is ON</option>
                <option value="no">No — auto-recharge is OFF</option>
              </select>
            </Field>
            <Field label="Recharge trigger (when balance falls below)">
              <input type="number" step="1" min={0} value={cfg.auto_recharge_trigger}
                data-testid="uk-trigger"
                onChange={(e) => setCfg({ ...cfg, auto_recharge_trigger: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]" />
              <p className="text-[10px] text-[#5A5A5A] mt-1">Recommended: 50+ for video workloads</p>
            </Field>
            <Field label="Recharge amount (credits added)">
              <input type="number" step="1" min={0} value={cfg.auto_recharge_amount}
                data-testid="uk-amount"
                onChange={(e) => setCfg({ ...cfg, auto_recharge_amount: parseFloat(e.target.value) || 0 })}
                className="w-full bg-[#121212] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-3 text-sm text-[#F4F4F0]" />
              <p className="text-[10px] text-[#5A5A5A] mt-1">Recommended: 50+ to cover a meaningful workload</p>
            </Field>
          </div>

          {err && <div className="mt-4 px-3 py-2 border border-red-500/40 bg-red-500/5 text-red-300 text-xs" data-testid="uk-save-error">{err}</div>}
          {saved && <div className="mt-4 px-3 py-2 border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 text-xs" data-testid="uk-save-ok">Saved · health refreshed</div>}

          <button onClick={onSave} disabled={saving} data-testid="uk-save"
            className="mt-5 inline-flex items-center justify-center gap-2 gold-sheen font-bold px-6 py-3 text-sm uppercase tracking-wider transition-all disabled:opacity-50">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save & re-check</>}
          </button>
        </div>

        <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-5 text-xs text-[#A0A0A0] leading-relaxed">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-2">How burn rate is calculated</p>
          <p>
            Burn rate comes from the <span className="text-[#F4F4F0]">provider_usage</span> collection — every Sora 2 call (success or fail) is logged when it runs.
            We multiply daily call count by an estimated 3 credits/standard clip and compare against your pasted balance to compute runway days.
            For a more accurate estimate, check Emergent's actual per-call credit consumption on Profile → Universal Key → Usage and we can tune the constant.
          </p>
        </div>
      </div>
    </AdminShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-2">{label}</span>
      {children}
    </label>
  );
}
